import React, { useEffect, useState } from "react";
import API from "../services/api";
import Sidebar from "../components/Sidebar";
import { toast } from "react-toastify";

function DailyChecklist() {
  const [dashboard, setDashboard] = useState(null);
  const [ocrImage, setOcrImage] = useState(null);
  const [ocrPreview, setOcrPreview] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);

  const [form, setForm] = useState({
    temperature: "",
    moisture_level: "",
    humidity: "",
    ph_level: "",

    water_added: false,
    water_quantity: "",

    food_added: false,
    food_type: "",
    food_quantity: "",

    worm_activity: "",
    odor_level: "",
    compost_color: "",
    food_consumption: "",

    ants_detected: false,
    flies_detected: false,
    maggots_detected: false,
    fungus_detected: false,
    leachate_present: false,

    bed_mixed: false,
    bedding_condition: "",
    harvest_ready: false,

    notes: ""
  });

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await API.get("/dashboard/summary");
      setDashboard(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  const handleCheckbox = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.checked
    });
  };

  const handleInput = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    setOcrImage(file);
    setOcrPreview(URL.createObjectURL(file));
  };

  const extractReadings = async () => {
    if (!ocrImage) {
      toast.error("Please upload an image first");
      return;
    }

    try {
      setOcrLoading(true);

      const data = new FormData();
      data.append("image", ocrImage);

      const res = await API.post(
        "/ocr/extract-readings",
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );

      setForm((prev) => ({
        ...prev,
        temperature:
          res.data.temperature || prev.temperature,

        moisture_level:
          res.data.moisture_level || prev.moisture_level,

        humidity:
          res.data.humidity || prev.humidity,

        ph_level:
          res.data.ph_level || prev.ph_level
      }));

      toast.success("Readings extracted successfully");
    } catch (err) {
      console.log(err);
      toast.error("OCR extraction failed");
    } finally {
      setOcrLoading(false);
    }
  };

  const submitChecklist = async () => {
    try {
      await API.post("/checklist/add", {
        ...form,
        cycle_id: dashboard?.cycle_id || null,
        checklist_date: new Date().toISOString().split("T")[0]
      });

      toast.success("Checklist submitted successfully");

      setForm({
        temperature: "",
        moisture_level: "",
        humidity: "",
        ph_level: "",
        water_added: false,
        water_quantity: "",
        food_added: false,
        food_type: "",
        food_quantity: "",
        worm_activity: "",
        odor_level: "",
        compost_color: "",
        food_consumption: "",
        ants_detected: false,
        flies_detected: false,
        maggots_detected: false,
        fungus_detected: false,
        leachate_present: false,
        bed_mixed: false,
        bedding_condition: "",
        harvest_ready: false,
        notes: ""
      });

      setOcrImage(null);
      setOcrPreview(null);

    } catch (err) {
      console.log(err);
      toast.error("Submission failed");
    }
  };

  const progress =
    dashboard?.total_days > 0
      ? Math.round(
          (dashboard.current_day / dashboard.total_days) * 100
        )
      : 0;

  return (
    <div className="dashboard-layout">
      <Sidebar />

      <div className="main-content">
        <div className="container-fluid">

          {/* Summary */}
          <div className="row g-4 mb-4">
            <div className="col-md-4">
              <div className="card dashboard-card text-center">
                <h6>Current Day</h6>
                <h2>
                  {dashboard?.current_day || 0}/
                  {dashboard?.total_days || 60}
                </h2>
                <small>
                  {(dashboard?.total_days || 60) -
                    (dashboard?.current_day || 0)} days remaining
                </small>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card dashboard-card text-center">
                <h6>Current Phase</h6>
                <span className="badge bg-primary p-2">
                  {dashboard?.current_phase || "Not Started"}
                </span>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card dashboard-card text-center">
                <h6>Progress</h6>
                <div className="progress mb-2">
                  <div
                    className="progress-bar bg-success"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <h4>{progress}%</h4>
              </div>
            </div>
          </div>

          {/* OCR Upload */}
          <div className="card dashboard-card mb-4">
            <h3>📷 Smart Meter Image Upload</h3>

            <input
              type="file"
              accept="image/*"
              className="form-control mb-3"
              onChange={handleImageUpload}
            />

            {ocrPreview && (
              <div className="mb-3">
                <img
                  src={ocrPreview}
                  alt="OCR Preview"
                  style={{
                    maxWidth: "300px",
                    borderRadius: "10px"
                  }}
                />
              </div>
            )}

            <button
              className="btn btn-primary"
              onClick={extractReadings}
              disabled={ocrLoading}
            >
              {ocrLoading
                ? "Extracting..."
                : "Extract Readings from Image"}
            </button>
          </div>

          {/* Environmental Readings */}
          <div className="card dashboard-card mb-4">
            <h3>🧪 Environmental Readings</h3>

            <div className="row g-3 mt-2">
              <div className="col-md-3">
                <input
                  type="number"
                  name="temperature"
                  className="form-control"
                  placeholder="Temperature °C"
                  value={form.temperature}
                  onChange={handleInput}
                />
              </div>

              <div className="col-md-3">
                <input
                  name="moisture_level"
                  className="form-control"
                  placeholder="Moisture Level"
                  value={form.moisture_level}
                  onChange={handleInput}
                />
              </div>

              <div className="col-md-3">
                <input
                  type="number"
                  name="humidity"
                  className="form-control"
                  placeholder="Humidity %"
                  value={form.humidity}
                  onChange={handleInput}
                />
              </div>

              <div className="col-md-3">
                <input
                  type="number"
                  step="0.1"
                  name="ph_level"
                  className="form-control"
                  placeholder="pH Level"
                  value={form.ph_level}
                  onChange={handleInput}
                />
              </div>
            </div>
          </div>

          {/* Feeding */}
          <div className="card dashboard-card mb-4">
            <h3>🍎 Feeding & Water</h3>

            <div className="row g-3">
              <div className="col-md-6">
                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    name="food_added"
                    className="form-check-input"
                    checked={form.food_added}
                    onChange={handleCheckbox}
                  />
                  <label>Add Food</label>
                </div>

                <input
                  name="food_type"
                  className="form-control mb-2"
                  placeholder="Food Type"
                  value={form.food_type}
                  onChange={handleInput}
                />

                <input
                  name="food_quantity"
                  className="form-control"
                  placeholder="Food Quantity"
                  value={form.food_quantity}
                  onChange={handleInput}
                />
              </div>

              <div className="col-md-6">
                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    name="water_added"
                    className="form-check-input"
                    checked={form.water_added}
                    onChange={handleCheckbox}
                  />
                  <label>Add Water</label>
                </div>

                <input
                  name="water_quantity"
                  className="form-control"
                  placeholder="Water Quantity"
                  value={form.water_quantity}
                  onChange={handleInput}
                />
              </div>
            </div>
          </div>

          {/* Compost Health */}
          <div className="card dashboard-card mb-4">
            <h3>🐛 Compost Health</h3>

            <div className="row g-3">
              <div className="col-md-3">
                <select
                  name="worm_activity"
                  className="form-select"
                  value={form.worm_activity}
                  onChange={handleInput}
                >
                  <option value="">Worm Activity</option>
                  <option>Low</option>
                  <option>Medium</option>
                  <option>High</option>
                </select>
              </div>

              <div className="col-md-3">
                <select
                  name="odor_level"
                  className="form-select"
                  value={form.odor_level}
                  onChange={handleInput}
                >
                  <option value="">Odor Level</option>
                  <option>None</option>
                  <option>Mild</option>
                  <option>Strong</option>
                  <option>Bad</option>
                </select>
              </div>

              <div className="col-md-3">
                <select
                  name="compost_color"
                  className="form-select"
                  value={form.compost_color}
                  onChange={handleInput}
                >
                  <option value="">Compost Color</option>
                  <option>Light Brown</option>
                  <option>Dark Brown</option>
                  <option>Black</option>
                </select>
              </div>

              <div className="col-md-3">
                <select
                  name="food_consumption"
                  className="form-select"
                  value={form.food_consumption}
                  onChange={handleInput}
                >
                  <option value="">Food Consumption</option>
                  <option>Not Consumed</option>
                  <option>Partially Consumed</option>
                  <option>Fully Consumed</option>
                </select>
              </div>
            </div>
          </div>

          {/* Pest Monitoring */}
          <div className="card dashboard-card mb-4">
            <h3>🐜 Pest / Issue Monitoring</h3>

            <div className="row">
              {[
                ["ants_detected", "Ants"],
                ["flies_detected", "Flies"],
                ["maggots_detected", "Maggots"],
                ["fungus_detected", "Fungus"],
                ["leachate_present", "Leachate Present"],
                ["bed_mixed", "Bed Mixed"],
                ["harvest_ready", "Harvest Ready"]
              ].map(([name, label]) => (
                <div className="col-md-3 mb-2" key={name}>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      name={name}
                      checked={form[name]}
                      className="form-check-input"
                      onChange={handleCheckbox}
                    />
                    <label>{label}</label>
                  </div>
                </div>
              ))}
            </div>

            <select
              name="bedding_condition"
              className="form-select mt-3"
              value={form.bedding_condition}
              onChange={handleInput}
            >
              <option value="">Bedding Condition</option>
              <option>Dry</option>
              <option>Optimal</option>
              <option>Wet</option>
              <option>Too Wet</option>
            </select>
          </div>

          {/* Notes */}
          <div className="card dashboard-card">
            <h3>📝 Daily Notes</h3>

            <textarea
              className="form-control mb-3"
              rows="5"
              placeholder="Record observations, issues, changes..."
              name="notes"
              value={form.notes}
              onChange={handleInput}
            />

            <button
              className="btn btn-success"
              onClick={submitChecklist}
            >
              Submit Checklist
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

export default DailyChecklist;